import { makeStyles } from "@mui/styles";
import { Button } from "components/Button/Button";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { registerExclusiveBlockingModal } from "components/Modal/exclusiveBlockingModal";
import { centeredPopoverRootStyle, Popover } from "components/Popover/Popover";
import { FieldResolution, StaleWriteFieldDiff } from "containers/Resources/staleWriteDiff";
import { useMemo, useState } from "react";
import ReactDOM from "react-dom";
import "./StaleWriteConflictDialog.scss";

/**
 * The "This record changed" dialog for an optimistic-concurrency (409) save
 * conflict. Shows every field that differs between the user's unsaved version
 * and the version now on the server, says who changed it, and lets the user
 * pick per field: keep theirs, keep mine, or edit a merged value.
 *
 * Built on the canonical dialog shell (ui-design-principle-modals-dialogs):
 * shared Popover centred on the page, 24px padding, bold in-body header, no
 * corner close button, Cancel-style action left / single primary action right
 * (ui-design-principle-buttons). The dialog does not close on an outside
 * click: it guards unsaved data, so only an explicit choice closes it.
 *
 * operator-task-fe-occ-conflict-dialog-show-diff-and-per-field-resolution
 */

export type StaleWriteConflictOutcome =
    { choice: "reload" } | { choice: "save"; resolutions: Record<string, FieldResolution> };

type RowChoice = "mine" | "theirs" | "edit";

const ORIGIN_LABEL: Record<StaleWriteFieldDiff["changedBy"], string> = {
    mine: "Changed by you",
    theirs: "Changed by them",
    both: "Changed by both",
};

export const defaultRowChoice = (row: StaleWriteFieldDiff): RowChoice =>
    row.changedBy === "theirs" ? "theirs" : "mine";

const isEmpty = (v: unknown) => v === null || v === undefined || v === "";

export const formatConflictValue = (v: unknown): string => {
    if (isEmpty(v)) {
        return "";
    }
    return typeof v === "string" ? v : JSON.stringify(v, null, 2);
};

/** Parse an edited value back to the field's type (JSON for non-strings). */
export const parseEditedValue = (
    row: StaleWriteFieldDiff,
    text: string,
): { ok: true; value: unknown } | { ok: false } => {
    const sample = row.mine ?? row.theirs;
    if (sample === undefined || sample === null || typeof sample === "string") {
        return { ok: true, value: text };
    }
    try {
        return { ok: true, value: JSON.parse(text) };
    } catch {
        return { ok: false };
    }
};

const ValueCell = ({ value }: { value: unknown }) =>
    isEmpty(value) ? (
        <span className="staleWriteConflict__empty">(empty)</span>
    ) : (
        <pre className="staleWriteConflict__value">{formatConflictValue(value)}</pre>
    );

interface StaleWriteConflictDialogProps {
    recordName?: string;
    diff: StaleWriteFieldDiff[];
    onReload: () => void;
    onSave: (resolutions: Record<string, FieldResolution>) => void;
}

export const StaleWriteConflictDialog = ({
    recordName,
    diff,
    onReload,
    onSave,
}: StaleWriteConflictDialogProps) => {
    const [choices, setChoices] = useState<Record<string, RowChoice>>(() =>
        Object.fromEntries(diff.map((row) => [row.path, defaultRowChoice(row)])),
    );
    const [edits, setEdits] = useState<Record<string, string>>({});

    const parsedEdits = useMemo(() => {
        const out: Record<string, { ok: true; value: unknown } | { ok: false }> = {};
        for (const row of diff) {
            if (choices[row.path] === "edit") {
                out[row.path] = parseEditedValue(row, edits[row.path] ?? "");
            }
        }
        return out;
    }, [diff, choices, edits]);
    const hasInvalidEdit = Object.values(parsedEdits).some((p) => !p.ok);

    const choose = (row: StaleWriteFieldDiff, choice: RowChoice) => {
        if (choice === "edit" && edits[row.path] === undefined) {
            const start = choices[row.path] === "theirs" ? row.theirs : row.mine;
            setEdits((prev) => ({ ...prev, [row.path]: formatConflictValue(start) }));
        }
        setChoices((prev) => ({ ...prev, [row.path]: choice }));
    };

    const save = () => {
        const resolutions: Record<string, FieldResolution> = {};
        for (const row of diff) {
            const choice = choices[row.path];
            if (choice === "edit") {
                const parsed = parsedEdits[row.path];
                if (parsed?.ok) {
                    resolutions[row.path] = { edited: parsed.value };
                }
            } else {
                resolutions[row.path] = choice;
            }
        }
        onSave(resolutions);
    };

    const useStyles = makeStyles(() => ({
        root: centeredPopoverRootStyle,
        paper: { maxHeight: "80vh", overflow: "auto" },
    }));
    const classes = useStyles();

    return (
        <Popover
            isOpen
            classes={{ root: classes.root, paper: classes.paper }}
            rootClassName="staleWriteConflict__popover"
            contentClassName="staleWriteConflict__content"
            anchorReference="none"
            closeOnInsideClick={false}
            closeOnOutsideClick={false}
        >
            <div className="staleWriteConflict__header">This record changed</div>
            <p className="staleWriteConflict__intro">
                {recordName ? <b>{recordName}</b> : "This record"} was changed by someone else after
                you opened it.{" "}
                {diff.length > 0
                    ? "Choose which version to keep for each field, then save."
                    : "Their changes do not touch the fields you are saving. Save to keep both."}
            </p>
            {diff.length > 0 && (
                <table className="staleWriteConflict__table">
                    <thead>
                        <tr>
                            <th>Field</th>
                            <th>Your version</th>
                            <th>Their version</th>
                        </tr>
                    </thead>
                    <tbody>
                        {diff.map((row) => {
                            const choice = choices[row.path];
                            const parsed = parsedEdits[row.path];
                            const label = row.attribute ?? row.field;
                            return (
                                <tr key={row.path} data-path={row.path}>
                                    <td className="staleWriteConflict__field">
                                        <div className="staleWriteConflict__fieldName">{label}</div>
                                        <div
                                            className={`staleWriteConflict__origin staleWriteConflict__origin--${row.changedBy}`}
                                        >
                                            {ORIGIN_LABEL[row.changedBy]}
                                        </div>
                                        <label className="staleWriteConflict__editToggle">
                                            <input
                                                type="radio"
                                                name={`conflict-${row.path}`}
                                                aria-label={`Edit ${label}`}
                                                checked={choice === "edit"}
                                                onChange={() => choose(row, "edit")}
                                            />
                                            Edit
                                        </label>
                                    </td>
                                    {choice === "edit" ? (
                                        <td colSpan={2} className="staleWriteConflict__editCell">
                                            <textarea
                                                aria-label={`Merged value for ${label}`}
                                                value={edits[row.path] ?? ""}
                                                onChange={(e) => {
                                                    // Read the value now: the updater runs
                                                    // after React restores the controlled
                                                    // textarea's DOM value.
                                                    const text = e.target.value;
                                                    setEdits((prev) => ({
                                                        ...prev,
                                                        [row.path]: text,
                                                    }));
                                                }}
                                            />
                                            {parsed && !parsed.ok && (
                                                <div className="staleWriteConflict__error">
                                                    Not valid JSON.
                                                </div>
                                            )}
                                        </td>
                                    ) : (
                                        (["mine", "theirs"] as const).map((side) => (
                                            <td
                                                key={side}
                                                className={`staleWriteConflict__choice${
                                                    choice === side
                                                        ? " staleWriteConflict__choice--selected"
                                                        : ""
                                                }`}
                                            >
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name={`conflict-${row.path}`}
                                                        aria-label={`${
                                                            side === "mine"
                                                                ? "Keep yours"
                                                                : "Keep theirs"
                                                        } for ${label}`}
                                                        checked={choice === side}
                                                        onChange={() => choose(row, side)}
                                                    />
                                                    <span className="staleWriteConflict__choiceLabel">
                                                        {side === "mine"
                                                            ? "Keep yours"
                                                            : "Keep theirs"}
                                                    </span>
                                                </label>
                                                <ValueCell value={row[side]} />
                                            </td>
                                        ))
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
            <div className="staleWriteConflict__buttons">
                <CancelButton label="Discard my changes" onClick={onReload} />
                <Button variation="primary" label="Save" disabled={hasInvalidEdit} onClick={save} />
            </div>
        </Popover>
    );
};

/**
 * Open the conflict dialog outside the React tree (from a saga) and resolve
 * with the user's choice. Registers as an exclusive blocking modal so the
 * unsaved-changes navigation guard does not stack its own popover on top.
 */
export const promptStaleWriteConflict = ({
    recordName,
    diff,
}: {
    recordName?: string;
    diff: StaleWriteFieldDiff[];
}): Promise<StaleWriteConflictOutcome> =>
    new Promise((resolve) => {
        const release = registerExclusiveBlockingModal();
        const host = document.createElement("div");
        document.body.appendChild(host);
        let settled = false;
        const settle = (outcome: StaleWriteConflictOutcome) => {
            if (settled) {
                return;
            }
            settled = true;
            ReactDOM.unmountComponentAtNode(host);
            host.remove();
            release();
            resolve(outcome);
        };
        ReactDOM.render(
            <StaleWriteConflictDialog
                recordName={recordName}
                diff={diff}
                onReload={() => settle({ choice: "reload" })}
                onSave={(resolutions) => settle({ choice: "save", resolutions })}
            />,
            host,
        );
    });
